import { Box } from '@chakra-ui/react';

import {
  ExperienceIndustryLeadersSection,
  IndustryLeaderCompanyWithLogo,
  IndustryLeaderLogo,
  transformStaticExperienceIndustryLeadersSectionProps,
} from '~/features/experience/components/experience-industry-leaders-section';
import { logger } from '~/features/logging/logger';

import { SchoolLpContent } from '../models/school-lp-content';

const CLOUD_INDUSTRY_LEADERS_THEME = {
  bgGradient: 'linear(252.73deg, #007697 0%, #0A0B0F 100%)',
  textureSrc: '/images/school-lp/cloud-computing/industry-leaders-texture.svg',
  borderColor: 'rgba(0,176,224,0.12)',
  descriptionColor: 'cyan.100',
} as const;

/** Near-square marks read much smaller than wordmarks at the same height. */
const LOGO_HEIGHT_OVERRIDES: Record<string, { base: string; md: string }> = {
  SUSE: { base: '32px', md: '48px' },
};

function renderSchoolIndustryLeaderLogo(company: IndustryLeaderCompanyWithLogo) {
  return <IndustryLeaderLogo company={company} height={LOGO_HEIGHT_OVERRIDES[company.name]} />;
}

type SchoolIndustryLeadersSectionProps = NonNullable<StaticSchoolIndustryLeadersSectionProps>;

export function SchoolIndustryLeadersSection(props: SchoolIndustryLeadersSectionProps) {
  return (
    <Box bg='white' paddingBottom={{ base: '64px', md: '96px' }} paddingTop={{ base: '32px', md: '72px' }}>
      <ExperienceIndustryLeadersSection
        {...props}
        {...CLOUD_INDUSTRY_LEADERS_THEME}
        renderLogo={renderSchoolIndustryLeaderLogo}
      />
    </Box>
  );
}

export async function transformStaticSchoolIndustryLeadersSectionProps({ content }: { content: SchoolLpContent }) {
  if (!content.industryLeaders) {
    return null;
  }

  const { title, description, companyNames } = content.industryLeaders;
  const props = await transformStaticExperienceIndustryLeadersSectionProps({
    title,
    description,
    companyNames,
  });

  const resolvedNames = new Set(props.companies.map((company) => company.name));
  const unresolvedCompanyNames = companyNames.filter((name) => !resolvedNames.has(name));

  if (unresolvedCompanyNames.length > 0) {
    logger.fatal('Configured school landing page industry-leader companies are missing from Sanity', {
      file: 'src/features/school-lp/components/school-industry-leaders-section.tsx',
      function: 'transformStaticSchoolIndustryLeadersSectionProps',
      unresolvedCompanyNames,
    });

    throw new Error(`Unresolved industry-leader companies: ${unresolvedCompanyNames.join(', ')}`);
  }

  return props;
}

export type StaticSchoolIndustryLeadersSectionProps = Awaited<
  ReturnType<typeof transformStaticSchoolIndustryLeadersSectionProps>
>;
